import { ChevronRight } from 'lucide-react';
import { createLogger } from '@xanectech/lib';
import { Link, useLocation } from 'react-router-dom';
import { set } from 'zod';
import { useEffect, useState } from 'react';

const logger = createLogger('Components - Breadcrumb');

export interface BreadcrumbItem {
  path: string;
  title: string;
}

export interface BreadcrumbProps {
  routeTitles: Record<string, string>;
  className?: string;
}

function Breadcrumb({ routeTitles, className = '' }: BreadcrumbProps) {
  const location = useLocation();
  const [showBreadcrumbs, setShowBreadcrumbs] = useState(true);
  const [routeStateName, setRouteStateName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!location) {
      return;
    }

    const stateBreadcrumbs =
      location.state && typeof location.state === 'object'
        ? (location.state as { showBreadcrumbs?: boolean }).showBreadcrumbs
        : true;
    setShowBreadcrumbs(stateBreadcrumbs ?? true);
    const routeState =
      location.state && typeof location.state === 'object'
        ? (location.state as { name?: string }).name
        : null;
    setRouteStateName(routeState ?? null);
    setLoading(false);
  }, [location]);

  const isUuidSegment = (segment: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      segment,
    ) || /^[0-9a-f]{32}$/i.test(segment);

  const formatSegmentTitle = (segment: string) =>
    segment
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ') || segment;

  const getRouteTitle = (path: string, segment: string) => {
    if (isUuidSegment(segment) && routeStateName) {
      return routeStateName;
    }

    if (routeTitles[path]) {
      return routeTitles[path];
    }

    const dynamicMatch = Object.entries(routeTitles).find(([route]) => {
      const routeParts = route.split('/').filter(Boolean);
      const pathParts = path.split('/').filter(Boolean);

      if (routeParts.length !== pathParts.length) return false;

      return routeParts.every(
        (routePart, index) =>
          routePart.startsWith(':') || routePart === pathParts[index],
      );
    });

    if (dynamicMatch) {
      return dynamicMatch[1];
    }

    return formatSegmentTitle(segment);
  };

  const currentPath = location.pathname;

  const pathSegments = currentPath.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  const previousSegment = pathSegments[pathSegments.length - 2];
  const actionSegments = new Set([
    'edit',
    'view',
    'analytics',
    'sharing',
    'submissions',
  ]);
  const visibleSegments =
    lastSegment &&
    actionSegments.has(lastSegment) &&
    previousSegment &&
    isUuidSegment(previousSegment)
      ? pathSegments.slice(0, -1)
      : pathSegments;

  const dataContractsIndex = pathSegments.lastIndexOf('data-contracts');
  const hasContractActionContext =
    dataContractsIndex >= 0 &&
    dataContractsIndex + 2 < pathSegments.length &&
    isUuidSegment(pathSegments[dataContractsIndex + 1] || '');

  const currentContractAction = hasContractActionContext
    ? pathSegments[dataContractsIndex + 2]
    : null;

  const showContractSwitcher =
    currentContractAction === 'analytics' ||
    currentContractAction === 'sharing';

  const contractBasePath =
    hasContractActionContext && dataContractsIndex >= 0
      ? '/' + pathSegments.slice(0, dataContractsIndex + 2).join('/')
      : '';

  const analyticsPath = contractBasePath
    ? `${contractBasePath}/analytics`
    : '/analytics';
  const sharingPath = contractBasePath
    ? `${contractBasePath}/sharing`
    : '/sharing';

  const breadcrumbs: BreadcrumbItem[] = visibleSegments.map(
    (segment, index) => {
      const path = '/' + visibleSegments.slice(0, index + 1).join('/');
      return {
        path,
        title: getRouteTitle(path, segment),
      };
    },
  );

  if (breadcrumbs.length === 0 || !showBreadcrumbs) {
    return null;
  }

  if (loading) {
    return null;
  }

  return (
    <nav
      className={`flex items-center text-sm text-muted-foreground ${className}`}
      aria-label='Breadcrumb'
    >
      {breadcrumbs.map((crumb, index) => (
        <span key={crumb.path} className='flex items-center'>
          {index > 0 && <ChevronRight className='mx-1 h-3 w-3' />}
          {index === breadcrumbs.length - 1 ? (
            <span className='text-foreground'>{crumb.title}</span>
          ) : (
            <Link
              to={crumb.path}
              className='hover:text-foreground transition-colors'
              state={{ name: routeStateName }}
            >
              {crumb.title}
            </Link>
          )}
        </span>
      ))}
      {showContractSwitcher && (
        <span className='flex items-center'>
          <ChevronRight className='mx-1 h-3 w-3' />
          {currentContractAction === 'analytics' ? (
            <span className='text-foreground'>Analytics</span>
          ) : (
            <Link
              to={analyticsPath}
              className='hover:text-foreground transition-colors'
              state={{ name: routeStateName }}
            >
              Analytics
            </Link>
          )}
          <span className='mx-1 text-muted-foreground/60'>/</span>
          {currentContractAction === 'sharing' ? (
            <span className='text-foreground'>Invitations &amp; Share</span>
          ) : (
            <Link
              to={sharingPath}
              className='hover:text-foreground transition-colors'
              state={{ name: routeStateName }}
            >
              Invitations &amp; Share
            </Link>
          )}
        </span>
      )}
    </nav>
  );
}

export { Breadcrumb };
